import { forwardRef, memo, useCallback, useRef, useState, type ChangeEvent } from 'react'
import {
  ChevronLeft, ChevronRight, LayoutGrid, Lock, LockOpen, Megaphone, Minus, MoreHorizontal,
  Newspaper, Plus, Redo2, Scissors, Undo2,
} from 'lucide-react'
import { useStore, useAgendaView, useActiveGrid, useValidation, useHasPermission } from '../../store/index.js'
import { useScheduleActions } from '../../hooks/useSchedule.js'
import { useGridUndo } from '../../hooks/useGridUndo.js'
import { formatDateShort, isToday } from '../../lib/utils.js'
import type { GridView } from '../../store/slices/scheduleSlice.js'
import {
  PPM_MIN, PPM_MAX, PPMW_MIN, PPMW_MAX, SLIDER_MIN, SLIDER_MAX,
  sliderToPpm, ppmToSlider, formatPpm, snapMinutesForPpm,
} from './useAgendaLayout.js'
import { SmartGridButton } from './SmartGridButton.js'
import { ContextMenu } from '../common/ContextMenu.js'

const VIEW_LABELS: Record<GridView, string> = {
  agenda: 'Jour',
  week: 'Semaine',
  playlist: 'Playlist',
}

// Ordre de la maquette : la vue par défaut d'abord.
const GRID_VIEWS: GridView[] = ['agenda', 'week', 'playlist']

/** Précision d'accrochage courante, en clair, pour l'infobulle du curseur. */
function snapLabel(ppm: number): string {
  const minutes = snapMinutesForPpm(ppm)
  if (minutes < 1) return `${Math.round(minutes * 60)} s`
  return `${minutes} min`
}

interface AgendaToolbarProps {
  channelName?: string
  onOpenAdBlocks?: () => void
  onOpenLiveBreak?: () => void
  onFillInfoservice?: () => void
  onOpenTemplate?: () => void
  onSmartGridApplied?: () => void
  locked?: boolean
  onToggleLock?: () => void
  canUseAi?: boolean
}

export const AgendaToolbar = memo(function AgendaToolbar({
  onOpenAdBlocks,
  onOpenLiveBreak,
  onFillInfoservice,
  onOpenTemplate,
  onSmartGridApplied,
  locked = false,
  onToggleLock,
  canUseAi = false,
}: AgendaToolbarProps) {
  const agendaView = useAgendaView()
  const activeGrid = useActiveGrid()
  const validation = useValidation()
  const canLifecycle = useHasPermission('GRID_LIFECYCLE_MANAGE')
  const canMove = useHasPermission('GRID_ITEM_MOVE')
  const { validateGrid, exportPlaylist } = useScheduleActions()
  const undo = useGridUndo()
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const hasErrors = validation?.issues.some(issue => issue.severity === 'ERROR')
  // La playlist n'a pas d'axe de temps : un curseur d'échelle n'y veut rien dire.
  const showScale = agendaView.view !== 'playlist'
  // Jour et Semaine ont chacune leur échelle : le curseur pilote celle de la
  // vue affichée, sinon régler la semaine casserait le réglage du jour.
  const isWeek = agendaView.view === 'week'
  const scaleMin = isWeek ? PPMW_MIN : PPM_MIN
  const scaleMax = isWeek ? PPMW_MAX : PPM_MAX
  const scaleValue = isWeek ? agendaView.pxPerMinuteWeek : agendaView.pxPerMinute

  const handlePreviousDay = useCallback(() => useStore.getState().shiftDay(-1), [])
  const handleNextDay = useCallback(() => useStore.getState().shiftDay(1), [])
  const handleToday = useCallback(() => useStore.getState().goToToday(), [])
  const handleViewChange = useCallback((view: GridView) => useStore.getState().setAgendaView(view), [])

  const applyScale = useCallback((sliderPosition: number) => {
    const store = useStore.getState()
    const week = store.agendaView.view === 'week'
    const ppm = sliderToPpm(sliderPosition, week ? PPMW_MIN : PPM_MIN, week ? PPMW_MAX : PPM_MAX)
    if (week) store.setPxPerMinuteWeek(ppm)
    else store.setPxPerMinute(ppm)
  }, [])
  const handleScaleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    applyScale(Number(event.target.value))
  }, [applyScale])
  const handleScaleStep = useCallback((direction: -1 | 1) => {
    const store = useStore.getState()
    const week = store.agendaView.view === 'week'
    const current = ppmToSlider(
      week ? store.agendaView.pxPerMinuteWeek : store.agendaView.pxPerMinute,
      week ? PPMW_MIN : PPM_MIN,
      week ? PPMW_MAX : PPM_MAX,
    )
    applyScale(current + direction * 50)
  }, [applyScale])
  const handleScaleOut = useCallback(() => handleScaleStep(-1), [handleScaleStep])
  const handleScaleIn = useCallback(() => handleScaleStep(1), [handleScaleStep])

  const handleValidate = useCallback(() => {
    if (activeGrid) validateGrid(activeGrid.id)
  }, [activeGrid, validateGrid])
  const handleExport = useCallback(() => {
    if (activeGrid) exportPlaylist(activeGrid.id)
  }, [activeGrid, exportPlaylist])

  const handleUndo = useCallback(() => { undo.undo().catch(console.error) }, [undo])
  const handleRedo = useCallback(() => { undo.redo().catch(console.error) }, [undo])

  const handleOpenMenu = useCallback(() => {
    const rect = menuButtonRef.current?.getBoundingClientRect()
    if (rect) setMenuPosition({ x: rect.right - 200, y: rect.bottom + 4 })
  }, [])
  const handleCloseMenu = useCallback(() => setMenuPosition(null), [])

  const scaleTitle = isWeek
    ? `Échelle de la semaine : ${formatPpm(scaleValue)}`
    // Le raccourci n'est visible nulle part ailleurs : l'infobulle du curseur
    // est le seul endroit où l'opérateur ira le chercher.
    : `Échelle de la timeline : ${formatPpm(scaleValue)} — accrochage ${snapLabel(scaleValue)} · Ctrl + molette sur la grille pour zoomer sous le pointeur`

  return (
    <div className="tp-toolbar agenda-toolbar" aria-label="Outils de la grille">
      <div className="tp-toolbar-group agenda-toolbar__date">
        <IconButton icon={<ChevronLeft size={14} />} title="Jour précédent" onClick={handlePreviousDay} />
        <div className="agenda-toolbar__date-label">{formatDateShort(agendaView.date)}</div>
        <IconButton icon={<ChevronRight size={14} />} title="Jour suivant" onClick={handleNextDay} />
        <button
          type="button"
          onClick={handleToday}
          className={`tp-button${isToday(agendaView.date) ? ' tp-button--info' : ''}`}
          title="Revenir à aujourd’hui"
          aria-pressed={isToday(agendaView.date)}
        >
          Aujourd’hui
        </button>
      </div>

      <div className="tp-toolbar-group tp-toolbar-group--display agenda-toolbar__display">
        <div className="tp-segmented" aria-label="Mode d’affichage">
          {GRID_VIEWS.map(view => (
            <ViewTab
              key={view}
              view={view}
              label={VIEW_LABELS[view]}
              active={agendaView.view === view}
              onChange={handleViewChange}
            />
          ))}
        </div>

        {showScale && (
          <div className="tp-zoom agenda-toolbar__scale" title={scaleTitle}>
            <IconButton icon={<Minus size={13} />} title="Réduire l’échelle" onClick={handleScaleOut} />
            <input
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={1}
              value={ppmToSlider(scaleValue, scaleMin, scaleMax)}
              onChange={handleScaleChange}
              aria-label={scaleTitle}
              aria-valuetext={formatPpm(scaleValue)}
            />
            <IconButton icon={<Plus size={13} />} title="Augmenter l’échelle" onClick={handleScaleIn} />
            <span className="agenda-toolbar__scale-label">{formatPpm(scaleValue)}</span>
          </div>
        )}
      </div>

      {/* Les actions de préparation passent en boutons-icônes : avec le curseur
          d'échelle, la rangée déborde si elles gardent leur libellé. */}
      <div className="tp-toolbar-group tp-toolbar-group--prepare agenda-toolbar__prepare">
        {onOpenTemplate && (
          <IconButton
            icon={<LayoutGrid size={14} />}
            title="Charger un modèle sur une semaine"
            onClick={onOpenTemplate}
          />
        )}
        <SmartGridButton onApplied={onSmartGridApplied} compact disabled={!canUseAi} />
        {onOpenAdBlocks && (
          <IconButton
            icon={<Megaphone size={14} />}
            title="Placer des spots en alternance avant les infoservices"
            onClick={onOpenAdBlocks}
          />
        )}
        {onOpenLiveBreak && (
          <IconButton
            icon={<Scissors size={14} />}
            title="Préparer le décrochage de direct de cette journée"
            onClick={onOpenLiveBreak}
          />
        )}
        {onFillInfoservice && (
          <IconButton
            icon={<Newspaper size={14} />}
            title="Remplir les trous d’au moins 1 minute avec l’infoservice sélectionné"
            onClick={onFillInfoservice}
          />
        )}
      </div>

      <div className="tp-toolbar-group tp-toolbar-group--publish agenda-toolbar__publish">
        {validation && (
          <div className={`tp-validation-badge${hasErrors ? ' is-error' : ''}`} role="status">
            {hasErrors
              ? `${validation.issues.filter(issue => issue.severity === 'ERROR').length} erreur(s)`
              : `Contrôlé · ${validation.coveragePercent}% couverture`}
          </div>
        )}

        {/* Annulation de la dernière action posée sur la grille. Le bouton reste
            visible en permanence — grisé, il dit qu'il n'y a rien à annuler,
            ce qui est une information en soi avant de lancer un modèle. */}
        <IconButton
          icon={<Undo2 size={14} />}
          title={undo.entry
            ? `Annuler « ${undo.entry.label} » · Ctrl + Z`
            : 'Aucune action de grille à annuler'}
          onClick={handleUndo}
          disabled={!undo.canUndo}
          className={undo.entry ? 'is-armed' : undefined}
        />

        {/* Rétablissement. Il n'apparaît qu'une fois quelque chose annulé : hors
            de ce moment-là il n'aurait rien à dire, là où le bouton d'annulation
            grisé renseigne en permanence sur ce qui est rattrapable. */}
        {undo.redoEntry && (
          <IconButton
            icon={<Redo2 size={14} />}
            title={`Rétablir « ${undo.redoEntry.label} » · Ctrl + Maj + Z`}
            onClick={handleRedo}
            disabled={!undo.canRedo}
            className="is-armed"
          />
        )}

        {onToggleLock && (
          <IconButton
            icon={locked ? <Lock size={14} /> : <LockOpen size={14} />}
            title={locked
              ? 'Cadenas verrouillé · Glisser-déposer impossible, ajout armé par Maj seulement · Cliquer pour déverrouiller'
              : 'Cadenas déverrouillé · Glisser-déposer actif, ajout toujours armé · Cliquer pour verrouiller'}
            onClick={onToggleLock}
            disabled={!canMove}
            pressed={!locked}
            className={!locked ? 'is-unlocked' : undefined}
          />
        )}

        {activeGrid && (
          <IconButton
            ref={menuButtonRef}
            icon={<MoreHorizontal size={14} />}
            title="Autres actions sur la grille"
            onClick={handleOpenMenu}
          />
        )}

      </div>

      {menuPosition && (
        <ContextMenu
          x={menuPosition.x}
          y={menuPosition.y}
          onClose={handleCloseMenu}
          actions={[
            { label: 'Contrôler la grille', disabled: !canLifecycle, onClick: handleValidate },
            { label: 'Exporter la playlist', disabled: !canLifecycle, onClick: handleExport },
          ]}
        />
      )}
    </div>
  )
})

const ViewTab = memo(function ViewTab({
  view,
  label,
  active,
  onChange,
}: {
  view: GridView
  label: string
  active: boolean
  onChange: (view: GridView) => void
}) {
  const handleClick = useCallback(() => onChange(view), [onChange, view])
  return (
    <button type="button" onClick={handleClick} className={`tp-segment${active ? ' is-active' : ''}`} aria-pressed={active}>
      {label}
    </button>
  )
})

/**
 * Bouton-icône de la barre d'outils. `title` et `aria-label` sont toujours
 * posés : sans libellé visible, c'est la seule chose qui rende la rangée
 * lisible et utilisable au lecteur d'écran.
 */
interface IconButtonProps {
  icon: React.ReactNode
  title: string
  onClick: () => void
  pressed?: boolean
  disabled?: boolean
  className?: string
}

const IconButton = memo(forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({
  icon, title, onClick, pressed, disabled, className,
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={pressed}
      disabled={disabled}
      className={`tp-icon-button agenda-toolbar__icon${className ? ` ${className}` : ''}`}
    >
      {icon}
    </button>
  )
}))
